import React from "react";
import { useTranslation } from "react-i18next";

interface LanguageSwitcherProps {
  className?: string;
  isMobile?: boolean;
  theme?: "dark" | "light";
}

export default function LanguageSwitcher({
  className = "",
  isMobile = false,
  theme = "dark",
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("en") ? "en" : "fr";

  const handleLanguageChange = (targetLang: "fr" | "en") => {
    if (targetLang === currentLang) return;
    i18n.changeLanguage(targetLang);
    try {
      localStorage.setItem("seva_lang", targetLang);
    } catch (e) {
      console.error("Failed to save language preference:", e);
    }
    document.documentElement.lang = targetLang;
  };

  if (isMobile) {
    return (
      <div className={`flex items-center justify-center gap-2 py-3 ${className}`}>
        <button
          type="button"
          onClick={() => handleLanguageChange("fr")}
          className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all duration-200 ${
            currentLang === "fr"
              ? "bg-[#037897] text-white shadow-sm"
              : "bg-gray-100 text-[#020220] hover:bg-gray-200"
          }`}
        >
          FR
        </button>
        <button
          type="button"
          onClick={() => handleLanguageChange("en")}
          className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all duration-200 ${
            currentLang === "en"
              ? "bg-[#037897] text-white shadow-sm"
              : "bg-gray-100 text-[#020220] hover:bg-gray-200"
          }`}
        >
          EN
        </button>
      </div>
    );
  }

  const containerClasses =
    theme === "light"
      ? "bg-gray-100 border border-gray-200"
      : "bg-white/10 backdrop-blur-sm border border-white/20";

  const inactiveBtnClasses =
    theme === "light"
      ? "text-gray-700 hover:text-[#037897] hover:bg-gray-200/60"
      : "text-white/80 hover:text-white hover:bg-white/5";

  return (
    <div
      className={`inline-flex items-center ${containerClasses} rounded-lg p-0.5 text-xs font-semibold ${className}`}
    >
      <button
        type="button"
        onClick={() => handleLanguageChange("fr")}
        className={`px-2.5 py-1 rounded-md transition-all duration-200 ${
          currentLang === "fr"
            ? "bg-[#037897] text-white shadow-sm font-bold"
            : inactiveBtnClasses
        }`}
        aria-label="Français"
      >
        FR
      </button>
      <button
        type="button"
        onClick={() => handleLanguageChange("en")}
        className={`px-2.5 py-1 rounded-md transition-all duration-200 ${
          currentLang === "en"
            ? "bg-[#037897] text-white shadow-sm font-bold"
            : inactiveBtnClasses
        }`}
        aria-label="English"
      >
        EN
      </button>
    </div>
  );
}
